import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  TemplateRef,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { UntypedFormControl } from '@angular/forms';
import { FormControl } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatLegacyButton as MatButton } from '@angular/material/legacy-button';
import { MatDrawer } from '@angular/material/sidenav';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';

import { Map } from 'mapbox-gl';
import { Observable, Subject, noop } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AccountService } from 'app/core';
import { User } from 'app/core/models/user.types';
import { Property } from 'app/modules/projects/projects.types';
import { PropertyPagination } from 'app/modules/properties/properties.types';
import { PropertiesService } from 'app/modules/properties/properties.service';
import { Account, FieldDef, SavedView } from 'app/core/models/account.types';
import { CalendarDrawerMode } from 'app/modules/calendar/calendar.types';

@Component({
  selector: 'properties-list-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertiesListMapComponent implements OnInit, OnDestroy {
  properties$: Observable<Property[]>;
  pagination: PropertyPagination;
  contactsCount: number;
  contactsTableColumns: string[];
  drawerMode: CalendarDrawerMode;
  drawerOpened: boolean;
  searchInputControl: UntypedFormControl;
  selectedPropety: Property;
  isLoading: boolean = false;
  queryFilters: any;
  exportingCsv: boolean = false;
  query: string = '';

  displayColumns: string[] = [];
  loopDisplayColumns: string[] = [];
  fieldDefs: FieldDef[] = [];
  data: any;

  @ViewChild('drawer', { static: true })
  matDrawer: MatDrawer;

  @ViewChild(MatPaginator)
  private _paginator: MatPaginator;

  @ViewChild(MatSort)
  private _sort: MatSort;

  // Private
  private _unsubscribeAll: Subject<void>;

  public map: Map;
  account: Account;
  user: User;

  tagsIsOpen = false;
  private _tagsPanelOverlayRef: OverlayRef;
  @ViewChild('tagsPanel')
  private _fieldDefsPanel: TemplateRef<any>;
  @ViewChild('tagsPanelOrigin')
  private _tagsPanelOrigin: MatButton;
  private _tagsPanelTemplatePortal: TemplatePortal;
  clickButton: boolean = false;
  quickContact: any;
  savedViews: SavedView[];
  savedView: SavedView;

  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _propertiesService: PropertiesService,
    private readonly _accountService: AccountService,
  ) {
    this._unsubscribeAll = new Subject();

    this.pagination = {
      length: 1,
      size: 1,
      page: 1,
      lastPage: 1,
      startIndex: 1,
      endIndex: 1 - 1,
    };

    this.drawerMode = 'side';
    this.drawerOpened = false;

    this.contactsCount = 0;
    this.contactsTableColumns = ['name', 'email', 'phone', 'tags', 'category_name', 'job', 'quick_view'];

    this._accountService.currentAccount.subscribe((accountData) => {
      this.account = accountData;
    });
  }

  ngOnInit(): void {
    this._activatedRoute.data.subscribe((data: Data) => {
      this.data = data;
    });

    this._accountService.savedViews$.pipe(takeUntil(this._unsubscribeAll)).subscribe((savedViews: SavedView[]) => {
      this.savedViews = savedViews;
    });

    this._accountService.savedView$.pipe(takeUntil(this._unsubscribeAll)).subscribe((savedView: SavedView) => {
      if (savedView) {
        this.savedView = savedView;
        this.displayColumns = this.savedView.definition?.table.fields;
      }
      this.savedView = savedView;
    });

    this.fieldDefs = [
      {
        name: 'name',
        label: 'Name',
        type: 'text',
      },
      {
        name: 'year_built',
        label: 'Year Built',
        type: 'year',
      },
      {
        name: 'occupancy',
        label: 'Occupancy',
        type: 'percent',
      },
      {
        name: 'unit_size',
        label: 'Num Units',
        type: 'number',
      },
      {
        name: 'num_parking_spaces',
        label: 'Num Parking Spaces',
        type: 'number',
      },
    ];

    // Get the contacts
    this.properties$ = this._propertiesService.properties$;
    this._propertiesService.properties$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(() => this._changeDetectorRef.markForCheck());

    // Get the pagination
    this._propertiesService.pagination$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((pagination: PropertyPagination) => {
        if (pagination) {
          // Update the pagination
          this.pagination = pagination;
          // Mark for check
          this._changeDetectorRef.markForCheck();
        }
      });
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions

    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  lookupField(name) {
    let fields = this.fieldDefs.filter((fieldDef) => fieldDef.name == name);
    if (fields.length > 0) {
      return fields[0];
    } else {
      return null;
    }
  }

  onUpdate(params, property) {
    const options = { ...params, id: property?.id };
    const selectColumn = [];
    selectColumn.push(Object.keys(params)[0]);
    this._propertiesService.saveProperty(options, property).pipe(takeUntil(this._unsubscribeAll)).subscribe(noop);
  }

  /**
   * Track by function for ngFor loops
   *
   * @param index
   * @param item
   */
  trackByFn(index: number, item: any): any {
    return item.id || index;
  }
}
